import React from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Spinner,
  Badge,
  Text,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDateTime } from '../lib/businessTime';

interface WebhookEvent {
  id: string;
  source: string;
  external_event_id: string;
  event_type: string;
  received_at: string | null;
  processed_at: string | null;
  error: string | null;
  payload: any;
  headers: any;
  status: 'success' | 'error' | 'pending';
}

interface Props {
  eventId: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    overflowY: 'auto',
    ...shorthands.padding('24px'),
    gap: '20px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  errorBox: {
    ...shorthands.padding('12px', '16px'),
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    ...shorthands.borderRadius('4px'),
    fontFamily: 'monospace',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '8px 16px',
    fontSize: '13px',
  },
  metaLabel: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 500,
  },
  metaValue: {
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  jsonBlock: {
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '500px',
    overflowY: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
});

const STATUS_INTENT: Record<WebhookEvent['status'], 'success' | 'danger' | 'warning'> = {
  success: 'success',
  error: 'danger',
  pending: 'warning',
};

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return formatBusinessDateTime(iso);
}

export default function WebhookEventDetailsPane({ eventId }: Props) {
  const styles = useStyles();

  const { data, isLoading, error } = useQuery<WebhookEvent>({
    queryKey: ['webhook-events', eventId],
    queryFn: async () => {
      const res = await apiFetch(`/webhooks/events/${eventId}`);
      if (!res.ok) throw new Error(`Failed to fetch event ${eventId}`);
      return res.json();
    },
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading event..." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={styles.container}>
        <div className={styles.errorBox}>
          {error ? (error as Error).message : 'Event not found'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Text className={styles.title}>{data.event_type}</Text>
          <Badge appearance="filled" color={STATUS_INTENT[data.status]}>
            {data.status}
          </Badge>
          <Badge appearance="outline">{data.source}</Badge>
        </div>
        <div className={styles.metaGrid}>
          <span className={styles.metaLabel}>Event ID</span>
          <span className={styles.metaValue}>{data.external_event_id}</span>
          <span className={styles.metaLabel}>Received</span>
          <span className={styles.metaValue}>{formatTimestamp(data.received_at)}</span>
          <span className={styles.metaLabel}>Processed</span>
          <span className={styles.metaValue}>{formatTimestamp(data.processed_at)}</span>
          <span className={styles.metaLabel}>Internal ID</span>
          <span className={styles.metaValue}>{data.id}</span>
        </div>
      </div>

      {data.error && (
        <div className={styles.section}>
          <span className={styles.sectionLabel}>Handler error</span>
          <div className={styles.errorBox}>{data.error}</div>
        </div>
      )}

      <div className={styles.section}>
        <span className={styles.sectionLabel}>Payload</span>
        <pre className={styles.jsonBlock}>{JSON.stringify(data.payload, null, 2)}</pre>
      </div>

      <div className={styles.section}>
        <span className={styles.sectionLabel}>Headers</span>
        <pre className={styles.jsonBlock}>{JSON.stringify(data.headers, null, 2)}</pre>
      </div>
    </div>
  );
}
